import { useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface Props {
  show: boolean;
  onHide: () => void;
  lat: number;
  lng: number;
  title?: string;
  address?: string;
}

export default function MapModal({ show, onHide, lat, lng, title, address }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!show || !mapRef.current) return;

    const timer = setTimeout(() => {
      if (!mapRef.current) return;

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }

      // Mappa con controlli
      const map = L.map(mapRef.current, {
        attributionControl: false,
        zoomControl: false,
      }).setView([lat, lng], 15);

      // Attribuzione _blank
      L.control.attribution({
        prefix: '<a href="https://leafletjs.com" target="_blank">Leaflet</a>',
      }).addTo(map);

      // Zoom in alto a destra
      L.control.zoom({ position: 'topright' }).addTo(map);

      // Scala in basso a sinistra
      L.control.scale({ imperial: false, position: 'bottomleft' }).addTo(map);

      // Layer switcher: standard + satellite-like
      const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
        maxZoom: 19,
      });

      const cartoLight = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://carto.com" target="_blank">CARTO</a>',
        maxZoom: 19,
      });

      const cartoDark = L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://carto.com" target="_blank">CARTO</a>',
        maxZoom: 19,
      });

      // Default layer
      cartoLight.addTo(map);

      // Layer control
      L.control.layers({
        'Standard': osmLayer,
        'Chiaro': cartoLight,
        'Scuro': cartoDark,
      }, {}, { position: 'topright' }).addTo(map);

      // Cerchio di raggio (500m)
      L.circle([lat, lng], {
        radius: 500,
        color: '#b42227',
        fillColor: '#b42227',
        fillOpacity: 0.08,
        weight: 1,
        dashArray: '5, 5',
      }).addTo(map);

      // Marker personalizzato rosso LSG
      const redIcon = L.divIcon({
        className: '',
        html: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 32 42">
          <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 26 16 26s16-14 16-26C32 7.2 24.8 0 16 0z" fill="#b42227"/>
          <circle cx="16" cy="15" r="7" fill="white"/>
        </svg>`,
        iconSize: [32, 42],
        iconAnchor: [16, 42],
        popupAnchor: [0, -42],
      });

      // Popup HTML personalizzato
      const navUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
      let popupHtml = '<div style="font-family:Inter,sans-serif;min-width:200px">';
      if (title) {
        popupHtml += `<div style="font-weight:600;font-size:14px;color:#333;margin-bottom:4px">${title}</div>`;
      }
      if (address) {
        popupHtml += `<div style="font-size:12px;color:#666;margin-bottom:6px">${address}</div>`;
      }
      popupHtml += `<div style="font-size:11px;color:#999;margin-bottom:8px">${lat.toFixed(5)}, ${lng.toFixed(5)}</div>`;
      popupHtml += `<a href="${navUrl}" target="_blank" rel="noopener" style="
        display:inline-flex;align-items:center;gap:4px;
        background:#b42227;color:#fff;text-decoration:none;
        padding:6px 12px;border-radius:4px;font-size:12px;font-weight:600;
      ">`;
      popupHtml += `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>`;
      popupHtml += `Naviga verso</a></div>`;

      const marker = L.marker([lat, lng], { icon: redIcon }).addTo(map);
      marker.bindPopup(popupHtml, { maxWidth: 280 }).openPopup();

      // Fullscreen toggle custom
      const fullscreenBtn = new L.Control({ position: 'topright' });
      fullscreenBtn.onAdd = function () {
        const div = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        div.innerHTML = '<a href="#" title="Schermo intero" style="display:flex;align-items:center;justify-content:center;width:30px;height:30px;font-size:16px">⛶</a>';
        div.onclick = function (e) {
          e.preventDefault();
          e.stopPropagation();
          const el = mapRef.current;
          if (!el) return;
          if (!document.fullscreenElement) {
            el.requestFullscreen();
          } else {
            document.exitFullscreen();
          }
        };
        return div;
      };
      fullscreenBtn.addTo(map);

      mapInstanceRef.current = map;
    }, 200);

    return () => {
      clearTimeout(timer);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [show, lat, lng, title, address]);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>{title || `${lat}, ${lng}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div ref={mapRef} style={{ height: 500, width: '100%' }} />
      </Modal.Body>
    </Modal>
  );
}
